// A persistent SAM "action card" rendered inline in the chat thread. SAM emits these when it
// lists a module (users/roles, …) with an LLM-authored title + caption. The card is recorded
// to the conversation trace, so it reappears on reload; "View" re-opens the workspace panel by
// re-fetching that module + filter (no workspace state is persisted).
import type { SamModule } from '@/components/SAM/samActionTypes';
import type { Rec } from '@/components/chat/ChatPills';

export interface ActionCardMessage {
  kind: 'action';
  id: string;
  module: SamModule;
  filter: Rec;
  title?: string;
  caption?: string;
  /** The record this view is bound to (e.g. get_engagement's property_id), when SAM's action
   *  carried one — threaded through to "View" so its recent-view entry matches the one the
   *  agent recorded, instead of a duplicate contextRef-less row. */
  recordId?: string | null;
}

const MODULE_ICON: Partial<Record<SamModule, string>> = {
  users: '👥',
  roles: '🛡️',
  communications: '✉️',
};

export const ActionCard = ({
  msg,
  onView,
}: {
  msg: ActionCardMessage;
  onView: (module: SamModule, filter: Rec) => void;
}) => {
  const heading = msg.title || `${msg.module.charAt(0).toUpperCase()}${msg.module.slice(1)}`;
  return (
    <div className="flex justify-start">
      <div className="max-w-[85%] w-full rounded-2xl border border-ui-border bg-ui-subtle px-4 py-3">
        <div className="flex items-start gap-2.5">
          <span className="text-lg leading-none mt-0.5" aria-hidden>{MODULE_ICON[msg.module] || '🗂️'}</span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-ui-text truncate">{heading}</p>
            {msg.caption && <p className="mt-0.5 text-xs text-ui-text3 leading-relaxed">{msg.caption}</p>}
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <button
            onClick={() => onView(msg.module, msg.filter || {})}
            className="rounded-lg btn-sheen px-3 py-1.5 text-xs font-medium text-on-primary transition"
          >
            View →
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActionCard;
